<script setup>
import { computed, onMounted, ref } from 'vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import { useAlert } from 'dashboard/composables';
import AiAPI from 'custom/api/ai';
import { useLocoI18n } from 'custom/composables/useLocoI18n';

// Assistant › Tools: HTTP endpoints the assistant may call (order status, stock, bookings...). It fills the
// parameters from the conversation; {{name}} in the URL takes a parameter, the rest go in the query or JSON body.
const props = defineProps({
  assistant: { type: Object, required: true },
});
const emit = defineEmits(['count']);

const { t } = useLocoI18n();

const METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];
const TYPES = ['string', 'number', 'integer', 'boolean'];
const NAME_FORMAT = /^[a-z][a-z0-9_]{1,40}$/;
const URL_EXAMPLE = 'https://api.example.com/orders/{{order_id}}';
const PARAMETER_EXAMPLE = '{{order_id}}';

const tools = ref([]);
const dialogRef = ref(null);
const editing = ref(null);
const isSaving = ref(false);

const canSave = computed(() => {
  const tool = editing.value;
  if (!tool) return false;
  return (
    NAME_FORMAT.test(tool.name) &&
    tool.description.trim() &&
    /^https?:\/\/\S+/.test(tool.url) &&
    tool.parameters.every(parameter => NAME_FORMAT.test(parameter.name))
  );
});

const errorMessage = error => error.response?.data?.error || error.message;

const fetchTools = async () => {
  const { data } = await AiAPI.tools(props.assistant.id);
  tools.value = data.payload;
  emit('count', tools.value.length);
};

const open = tool => {
  editing.value = tool
    ? {
        ...tool,
        parameters: tool.parameters.map(parameter => ({
          ...parameter,
          required: parameter.required !== false,
        })),
        headers: tool.header_names.map(name => ({
          name,
          value: '',
          saved: true,
        })),
      }
    : {
        name: '',
        description: '',
        http_method: 'GET',
        url: '',
        active: true,
        parameters: [],
        headers: [],
      };
  dialogRef.value.open();
};

const save = async () => {
  const tool = editing.value;
  isSaving.value = true;
  try {
    const payload = {
      name: tool.name,
      description: tool.description,
      http_method: tool.http_method,
      url: tool.url,
      active: tool.active,
      parameters: tool.parameters,
      headers: Object.fromEntries(
        tool.headers
          .filter(header => header.name.trim())
          .map(header => [header.name.trim(), header.value])
      ),
    };
    if (tool.id) await AiAPI.updateTool(props.assistant.id, tool.id, payload);
    else await AiAPI.createTool(props.assistant.id, payload);
    dialogRef.value.close();
    await fetchTools();
  } catch (error) {
    useAlert(errorMessage(error));
  } finally {
    isSaving.value = false;
  }
};

const toggle = async (tool, active) => {
  try {
    await AiAPI.updateTool(props.assistant.id, tool.id, { active });
    tool.active = active;
  } catch (error) {
    useAlert(errorMessage(error));
  }
};

const remove = async tool => {
  try {
    await AiAPI.deleteTool(props.assistant.id, tool.id);
    await fetchTools();
  } catch (error) {
    useAlert(errorMessage(error));
  }
};

onMounted(fetchTools);
</script>

<template>
  <div class="flex flex-col gap-6 pb-8">
    <div class="flex flex-wrap items-start gap-4">
      <p class="text-sm text-n-slate-11 flex-1 min-w-0">
        {{ t('LOCO.AI.TOOLS.HELP') }}
      </p>
      <Button
        sm
        icon="i-lucide-plus"
        :label="t('LOCO.AI.TOOLS.NEW')"
        @click="open(null)"
      />
    </div>

    <p v-if="!tools.length" class="text-sm text-n-slate-11">
      {{ t('LOCO.AI.TOOLS.EMPTY') }}
    </p>

    <ul v-else class="flex flex-col divide-y divide-n-weak">
      <li
        v-for="tool in tools"
        :key="tool.id"
        class="flex items-start gap-3 py-3"
      >
        <div class="flex flex-col gap-0.5 min-w-0 flex-1">
          <span class="text-sm font-mono text-n-slate-12">{{ tool.name }}</span>
          <span class="text-sm text-n-slate-11 line-clamp-2">
            {{ tool.description }}
          </span>
          <span class="text-xs font-mono text-n-slate-10 truncate">
            {{ `${tool.http_method} ${tool.url}` }}
          </span>
        </div>
        <div class="flex items-center gap-1 flex-shrink-0">
          <Switch
            :model-value="tool.active"
            @update:model-value="value => toggle(tool, value)"
          />
          <Button
            xs
            ghost
            slate
            icon="i-lucide-pencil"
            :title="t('LOCO.AI.TOOLS.EDIT')"
            @click="open(tool)"
          />
          <Button
            xs
            ghost
            ruby
            icon="i-lucide-trash-2"
            :title="t('LOCO.AI.TOOLS.DELETE')"
            @click="remove(tool)"
          />
        </div>
      </li>
    </ul>

    <Dialog
      ref="dialogRef"
      width="2xl"
      overflow-y-auto
      :title="editing?.id ? t('LOCO.AI.TOOLS.EDIT') : t('LOCO.AI.TOOLS.NEW')"
      :confirm-button-label="t('LOCO.AI.TOOLS.SAVE')"
      :disable-confirm-button="!canSave"
      :is-loading="isSaving"
      @confirm="save"
    >
      <div v-if="editing" class="flex flex-col gap-4">
        <label class="flex flex-col gap-1 text-sm">
          <span class="font-medium text-n-slate-12">
            {{ t('LOCO.AI.TOOLS.NAME') }}
          </span>
          <input
            v-model="editing.name"
            type="text"
            class="!mb-0 font-mono"
            :placeholder="t('LOCO.AI.TOOLS.NAME_PLACEHOLDER')"
          />
          <span class="text-xs text-n-slate-11">
            {{ t('LOCO.AI.TOOLS.NAME_HELP') }}
          </span>
        </label>
        <label class="flex flex-col gap-1 text-sm">
          <span class="font-medium text-n-slate-12">
            {{ t('LOCO.AI.TOOLS.DESCRIPTION') }}
          </span>
          <textarea
            v-model="editing.description"
            rows="2"
            class="!mb-0 resize-y"
            :placeholder="t('LOCO.AI.TOOLS.DESCRIPTION_PLACEHOLDER')"
          />
        </label>
        <div class="grid gap-2 grid-cols-[7rem_1fr]">
          <select v-model="editing.http_method" class="!mb-0">
            <option v-for="method in METHODS" :key="method" :value="method">
              {{ method }}
            </option>
          </select>
          <input
            v-model="editing.url"
            type="url"
            class="!mb-0 font-mono text-sm"
            :placeholder="URL_EXAMPLE"
          />
        </div>
        <p class="text-xs text-n-slate-11 -mt-2">
          {{ t('LOCO.AI.TOOLS.URL_HELP', { example: PARAMETER_EXAMPLE }) }}
        </p>

        <div class="flex flex-col gap-2">
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('LOCO.AI.TOOLS.PARAMETERS') }}
          </span>
          <div
            v-for="(parameter, index) in editing.parameters"
            :key="index"
            class="grid gap-2 grid-cols-[1fr_7rem_2fr_auto_auto] items-center"
          >
            <input
              v-model="parameter.name"
              type="text"
              class="!mb-0 font-mono text-sm"
              :placeholder="t('LOCO.AI.TOOLS.PARAMETER_NAME')"
            />
            <select v-model="parameter.type" class="!mb-0 text-sm">
              <option v-for="type in TYPES" :key="type" :value="type">
                {{ type }}
              </option>
            </select>
            <input
              v-model="parameter.description"
              type="text"
              class="!mb-0 text-sm"
              :placeholder="t('LOCO.AI.TOOLS.PARAMETER_DESCRIPTION')"
            />
            <label class="flex items-center gap-1 text-xs text-n-slate-11">
              <input v-model="parameter.required" type="checkbox" />
              {{ t('LOCO.AI.TOOLS.REQUIRED') }}
            </label>
            <Button
              type="button"
              xs
              ghost
              slate
              icon="i-lucide-x"
              @click="editing.parameters.splice(index, 1)"
            />
          </div>
          <Button
            type="button"
            xs
            link
            icon="i-lucide-plus"
            class="self-start"
            :label="t('LOCO.AI.TOOLS.ADD_PARAMETER')"
            @click="
              editing.parameters.push({
                name: '',
                type: 'string',
                description: '',
                required: true,
              })
            "
          />
        </div>

        <div class="flex flex-col gap-2">
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('LOCO.AI.TOOLS.HEADERS') }}
          </span>
          <span class="text-xs text-n-slate-11">
            {{ t('LOCO.AI.TOOLS.HEADERS_HELP') }}
          </span>
          <div
            v-for="(header, index) in editing.headers"
            :key="index"
            class="grid gap-2 grid-cols-[1fr_2fr_auto] items-center"
          >
            <input
              v-model="header.name"
              type="text"
              class="!mb-0 font-mono text-sm"
              :placeholder="t('LOCO.AI.TOOLS.HEADER_NAME')"
            />
            <input
              v-model="header.value"
              type="password"
              autocomplete="off"
              class="!mb-0 text-sm"
              :placeholder="
                header.saved
                  ? t('LOCO.AI.SETTINGS.KEY_SAVED')
                  : t('LOCO.AI.TOOLS.HEADER_VALUE')
              "
            />
            <Button
              type="button"
              xs
              ghost
              slate
              icon="i-lucide-x"
              @click="editing.headers.splice(index, 1)"
            />
          </div>
          <Button
            type="button"
            xs
            link
            icon="i-lucide-plus"
            class="self-start"
            :label="t('LOCO.AI.TOOLS.ADD_HEADER')"
            @click="editing.headers.push({ name: '', value: '' })"
          />
        </div>
      </div>
    </Dialog>
  </div>
</template>
